// Daily report soft-delete and restore API.
// Both operations use transactional SECDEF RPCs (mig 067) that mutate
// the record and insert an Activity event in one transaction.

import type {RpcEnvelope, SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

const TABLE_TO_ENTITY: Record<string, string | undefined> = {
  poultry_dailys: 'poultry.daily',
  layer_dailys: 'layer.daily',
  egg_dailys: 'egg.daily',
  pig_dailys: 'pig.daily',
  cattle_dailys: 'cattle.daily',
  sheep_dailys: 'sheep.daily',
};

export const LIGHT_DAILY_REPORT_EDIT_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

// Every RPC below accepts EITHER a table name or an already-resolved entity
// type, which is what makes the fallback below load-bearing rather than
// defensive: an unmapped value is passed through unchanged.
function resolveEntityType(tableOrEntityType: string): string {
  return TABLE_TO_ENTITY[tableOrEntityType] || tableOrEntityType;
}

// The two columns this module reads off a loaded daily row. Both are the
// database's types, not invented ones: `owner_profile_id` is the uuid mig 091
// compares against auth.uid(), `submitted_at` the timestamptz the Light edit
// window is measured from. Every other column stays `unknown` — the pages load
// whole rows and this module has no business describing them.
export type DailyReportRecord = {
  owner_profile_id?: string | null;
  submitted_at?: string | null;
  [column: string]: unknown;
};

// AuthContext publishes `null` (loading) | `false` (signed out) | the resolved
// identity, so all three are admitted here. The reads below use a truthiness
// check rather than `authState?.role`, because optional chaining short-circuits
// on null/undefined only — `false?.role` would still be a property access. The
// truthiness form behaves identically at runtime and is expressible.
export type DailyReportAuthState =
  | {role?: string | null; user?: {id?: string | null} | null}
  | null
  | false
  | undefined;

export function isWithinLightDailyReportEditWindow(
  record: DailyReportRecord | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  const submittedAt = record?.submitted_at;
  if (!submittedAt) return false;
  const submittedMs = new Date(submittedAt).getTime();
  if (!Number.isFinite(submittedMs)) return false;
  return nowMs - submittedMs <= LIGHT_DAILY_REPORT_EDIT_WINDOW_MS;
}

export async function softDeleteDailyReport(
  sb: SupabaseClient,
  tableOrEntityType: string,
  id: string,
  entityLabel?: string | null,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('softDeleteDailyReport: sb required');
  const entityType = resolveEntityType(tableOrEntityType);
  const {data, error} = await sb.rpc('soft_delete_daily_report', {
    p_entity_type: entityType,
    p_entity_id: id,
    p_entity_label: entityLabel || null,
  });
  if (error) throw new Error(`softDeleteDailyReport: ${error.message || String(error)}`);
  return data;
}

// Roles that may edit/delete ANY record (not just their own). Light is the
// only authenticated role scoped to its own records. Mirrors the closed
// allowlist of update_daily_report / soft_delete_daily_report (migration 221):
// equipment_tech edits and deletes no daily report (owner decision D3), and
// inactive or any unknown role can do neither.
const PRIVILEGED_EDIT_ROLES = ['admin', 'management', 'farm_team'];

// Server-mirrored ownership gate for showing edit/delete affordances. The DB
// RPCs are the real enforcement (mig 091 + later window migrations); this only
// decides whether to render controls the server would accept. For Light users,
// `owner_profile_id` and `submitted_at` must be present in the loaded row.
export function canEditOwnRecord(
  authState: DailyReportAuthState,
  record: DailyReportRecord | null | undefined,
): boolean {
  const role = authState ? authState.role : undefined;
  if (!role || role === 'inactive') return false;
  if (PRIVILEGED_EDIT_ROLES.includes(role)) return true;
  if (role === 'light') {
    const uid = authState ? authState.user?.id : undefined;
    return !!uid && !!record && record.owner_profile_id === uid && isWithinLightDailyReportEditWindow(record);
  }
  return false;
}

export function canDeleteDailyReport(
  authState: DailyReportAuthState,
  record: DailyReportRecord | null | undefined,
): boolean {
  const role = authState ? authState.role : undefined;
  if (!role) return false;
  // Light may delete only its own records; the privileged roles, any record.
  // A closed list, like the server's: equipment_tech, inactive and any role
  // added later get no Delete control.
  if (role === 'light') return canEditOwnRecord(authState, record);
  return PRIVILEGED_EDIT_ROLES.includes(role);
}

// Edit a daily report through the ownership-enforced SECDEF RPC (mig 091).
// The RPC applies a server-side column allowlist + computes the field.updated
// Activity diff in one transaction; `patch` is the same record object the
// pages already build. Throws on RPC error (incl. the Light ownership reject
// and the daily duplicate constraint, so callers can surface friendly copy).
export async function updateDailyReport(
  sb: SupabaseClient,
  tableOrEntityType: string,
  id: string,
  patch: Record<string, unknown>,
  {entityLabel}: {entityLabel?: string | null} = {},
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('updateDailyReport: sb required');
  const entityType = resolveEntityType(tableOrEntityType);
  const {data, error} = await sb.rpc('update_daily_report', {
    p_entity_type: entityType,
    p_entity_id: id,
    p_patch: patch,
    p_entity_label: entityLabel || null,
  });
  if (error) throw new Error(error.message || String(error));
  return data;
}

// The RAW PostgREST pair the two submit helpers below resolve with. `data` is
// the RPC's jsonb; `error` is deliberately only narrowed to what the offline
// queue reads structurally, because the classification it performs branches on
// `code`/`status`/`message`, none of which this repository has generated types
// for.
export type RawRpcResult = {
  data: unknown;
  error: (SupabaseErrorLike & {code?: string; status?: number}) | null;
};

// Tier-2 write boundary (migration 192): cattle daily reports are created ONLY
// through this SECDEF RPC. Direct INSERT on cattle_dailys is revoked from
// anon + authenticated and the table's authenticated policy is SELECT-only, so
// any remaining direct PostgREST insert on that table fails in production.
// The cp2_daily_writes_via_rpc static guard enforces that there are none.
//
// Unlike the other helpers in this file this returns the RAW supabase
// `{data, error}` pair instead of throwing. That is deliberate and
// load-bearing: the offline queue in useOfflineSubmit.js classifies the
// PostgREST error by SQLSTATE + constraint name —
//   cattle_dailys_client_submission_id_uq  -> idempotent replay, mark synced
//   cattle_dailys_active_daily_identity_uq -> superseded, discard
// Wrapping those in `new Error(message)` would drop `error.code`/`error.status`
// and silently break offline replay classification.
export async function submitCattleDailyReport(
  sb: SupabaseClient,
  record: Record<string, unknown>,
): Promise<RawRpcResult> {
  if (!sb) throw new Error('submitCattleDailyReport: sb required');
  return sb.rpc('submit_cattle_daily_report', {p_record: record});
}

// Tier-2 write boundary (migration 194): sheep daily reports are created ONLY
// through this SECDEF RPC. Same raw {data, error} contract as
// submitCattleDailyReport above, and for the same reason: useOfflineSubmit.js
// classifies the PostgREST error by SQLSTATE + constraint name
// (sheep_dailys_client_submission_id_uq / sheep_dailys_active_daily_identity_uq).
export async function submitSheepDailyReport(
  sb: SupabaseClient,
  record: Record<string, unknown>,
): Promise<RawRpcResult> {
  if (!sb) throw new Error('submitSheepDailyReport: sb required');
  return sb.rpc('submit_sheep_daily_report', {p_record: record});
}

export async function restoreDailyReport(
  sb: SupabaseClient,
  tableOrEntityType: string,
  id: string,
  entityLabel?: string | null,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('restoreDailyReport: sb required');
  const entityType = resolveEntityType(tableOrEntityType);
  const {data, error} = await sb.rpc('restore_daily_report', {
    p_entity_type: entityType,
    p_entity_id: id,
    p_entity_label: entityLabel || null,
  });
  if (error) throw new Error(`restoreDailyReport: ${error.message || String(error)}`);
  return data;
}
